/**
 * Shared answer-grading for practice/quiz questions.
 *
 * Used by BOTH ChapterQuiz (the "Quiz" tab) and PracticeBlock (the "Questions"
 * tab) so a given answer is graded identically wherever it appears. Grading is
 * intentionally lenient for free-answer kinds (numeric compares as numbers;
 * short/journal-entry accept substring matches) to avoid false negatives on
 * answers that are correct in substance.
 */

/** Normalise a string for exact-match comparison (MCQ + short-answer fallback). */
export function normalise(s: string): string {
  return s
    .toLowerCase()
    .replace(/[₹,\s%]/g, '') // strip currency, commas, whitespace, percent
    .replace(/[.,!?;:]+$/, '') // strip trailing punctuation
    .trim();
}

/** Parse a number from a user/correct string (strips ₹, commas, spaces, %). */
function parseNumeric(s: string): number | null {
  const cleaned = s.replace(/[₹,\s%]/g, '').trim();
  const n = Number(cleaned);
  return isFinite(n) && cleaned !== '' ? n : null;
}

/** Numeric grading: compare as numbers; fall back to normalised string match. */
function compareNumeric(given: string, correct: string): boolean {
  const gn = parseNumeric(given);
  const cn = parseNumeric(correct);
  if (gn !== null && cn !== null) return gn === cn;
  return normalise(given) === normalise(correct);
}

/** Lenient short/journal-entry grading: normalised exact OR either-contains-other. */
function matchShortAnswer(given: string, correct: string): boolean {
  const g = normalise(given);
  const c = normalise(correct);
  if (!g) return false;
  return g === c || g.includes(c) || c.includes(g);
}

/** Grade a student answer against the correct value for a given question kind. */
export function gradeAnswer(value: string, correct: string, kind: string): boolean {
  if (kind === 'mcq') return normalise(value) === normalise(correct);
  if (kind === 'numeric') return compareNumeric(value, correct);
  return matchShortAnswer(value, correct); // short / journal-entry / fallback
}
